import { useEffect, useState } from 'react'

interface Participant {
  id: string
  image?: string | null
  imageWithBackground?: string | null
  campEndDate?: string | null
}

export type ParticipantImageStatus = 'NO_IMAGE' | 'FULLY_PROCESSED' | 'LEGACY_NEEDS_PROCESSING' | 'PROCESSING'

export const getParticipantImageStatus = (participant: Participant): ParticipantImageStatus => {
  // Check if we have both processed and original images (fully processed)
  if (participant.image && participant.imageWithBackground) {
    // Check if the processed image is not a default placeholder
    if (!participant.image.includes('default_talentcard.png')) {
      return 'FULLY_PROCESSED'
    }
  }

  // Check if we have processed image but no original image (unusual case)
  if (participant.image && !participant.imageWithBackground) {
    // Check if this is a default placeholder image
    if (participant.image.includes('default_talentcard.png')) {
      return 'NO_IMAGE'
    }
    return 'FULLY_PROCESSED' // Has processed image, consider it done
  }

  // Check if we have original image but no processed image (LEGACY CASE)
  if (!participant.image && participant.imageWithBackground) {
    // Check if the original image is not a default placeholder
    if (participant.imageWithBackground.includes('default_talentcard.png')) {
      return 'NO_IMAGE'
    }

    // FOR NEW TALENT CARDS: Always process images to remove background
    // The user wants new talent cards to ALWAYS show images without background
    // regardless of when the camp ended
    return 'LEGACY_NEEDS_PROCESSING'
  }

  // No images at all
  return 'NO_IMAGE'
}

export const useBackgroundRemovalDetection = (participant: Participant | null | undefined) => {
  const [status, setStatus] = useState<ParticipantImageStatus>('NO_IMAGE')
  const [processingParticipants, setProcessingParticipants] = useState<Set<string>>(new Set())

  useEffect(() => {
    if (!participant) {
      setStatus('NO_IMAGE')
      return
    }

    const currentStatus = getParticipantImageStatus(participant)

    // Check if this participant is currently being processed
    if (currentStatus === 'LEGACY_NEEDS_PROCESSING' && processingParticipants.has(participant.id)) {
      setStatus('PROCESSING')
    } else {
      setStatus(currentStatus)
    }
  }, [participant, processingParticipants])

  const markAsProcessing = (participantId: string) => {
    setProcessingParticipants(prev => new Set([...prev, participantId]))
  }

  const markAsCompleted = (participantId: string) => {
    setProcessingParticipants(prev => {
      const newSet = new Set(prev)
      newSet.delete(participantId)
      return newSet
    })
  }

  return {
    status,
    isLegacyParticipant: status === 'LEGACY_NEEDS_PROCESSING',
    needsProcessing: status === 'LEGACY_NEEDS_PROCESSING',
    isProcessing: status === 'PROCESSING',
    markAsProcessing,
    markAsCompleted
  }
}
